import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { industries } from "../../lib/content";

export default function IndustriesBento() {
    // Clean 2-row layout — no overlap
    const spans = [
        "md:col-span-8 md:row-span-2 min-h-[360px] md:min-h-[520px]", // Advanced Manufacturing - big, spans 2 rows
        "md:col-span-4 min-h-[250px]", // Data centers
        "md:col-span-4 min-h-[250px]", // Healthcare
        "md:col-span-5 min-h-[280px]", // Highrise
        "md:col-span-4 min-h-[280px]", // Education
        "md:col-span-3 min-h-[280px]", // Commercial
    ];

    return (
        <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="industries-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-6">
                            <span className="w-8 h-px bg-cyan-400/60" /> Industries
                        </div>
                        <h2 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter">
                            Built for the most <span className="text-blue-400">demanding</span> environments on Earth.
                        </h2>
                    </div>
                    <Link
                        to="/industries"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors group whitespace-nowrap"
                        data-testid="industries-view-all"
                    >
                        Explore all sectors
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {industries.map((ind, i) => (
                        <motion.div
                            key={ind.slug}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-blue-500/40 transition-all duration-500 ${spans[i]}`}
                            data-testid={`industry-card-${ind.slug}`}
                        >
                            <img
                                src={ind.image}
                                alt={ind.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/30" />
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-transparent to-transparent" />

                            <div className="relative h-full p-7 md:p-8 flex flex-col justify-between">
                                <div className="flex items-start justify-between">
                                    <span className="font-mono text-[10px] tracking-[0.3em] text-cyan-400/80">{ind.code}</span>
                                    <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-cyan-400 group-hover:rotate-45 transition-all duration-300" />
                                </div>
                                <div>
                                    <h3 className="font-heading font-black text-white text-2xl md:text-3xl leading-tight tracking-tight">
                                        {ind.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-300/80 leading-relaxed max-w-md">{ind.short}</p>
                                    {i === 0 && (
                                        <div className="mt-6 flex flex-wrap gap-2">
                                            {ind.sub.map((s) => (
                                                <span
                                                    key={s}
                                                    className="inline-flex items-center px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-xs text-slate-200"
                                                >
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
